import { useCallback } from "react";
import { toast } from "sonner";
import { publicDonationPaymentService } from "@project/services/donate/donation-payment.service";
import type { DonationCreateOrderPayload } from "@project/types/donation-payment";

export interface RazorpayPaymentSuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  order_id: string;
  amount: number;
  currency?: string;
  key?: string;
  name?: string;
  description?: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  onSuccess: (response: RazorpayPaymentSuccess) => void | Promise<void>;
  onDismiss?: () => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadCheckoutScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.Razorpay) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
  return scriptPromise;
}

export function getRazorpayKey(): string | undefined {
  return import.meta.env.VITE_RAZORPAY_KEY_ID as string | undefined;
}

let pendingSuccessCallback:
  | ((response: RazorpayPaymentSuccess) => void | Promise<void>)
  | null = null;

export async function openRazorpayCheckout(
  options: RazorpayCheckoutOptions
): Promise<void> {
  await loadCheckoutScript();

  const key = options.key ?? getRazorpayKey();
  if (!key) {
    throw new Error(
      "Razorpay is not configured. Set VITE_RAZORPAY_KEY_ID in your .env file."
    );
  }

  const Razorpay = window.Razorpay;
  if (!Razorpay) {
    throw new Error("Razorpay checkout script is not available.");
  }

  pendingSuccessCallback = options.onSuccess;

  const rzp = new Razorpay({
    key,
    order_id: options.order_id,
    amount: options.amount,
    currency: options.currency ?? "INR",
    name: options.name ?? "Jivandhara Old Age Home",
    description: options.description ?? "Donation Payment",
    prefill: options.prefill,
    handler(response: RazorpayPaymentSuccess) {
      const callback = pendingSuccessCallback;
      pendingSuccessCallback = null;
      if (!callback) {
        return;
      }
      void Promise.resolve(callback(response)).catch(error => {
        console.error("Razorpay onSuccess error:", error);
      });
    },
    modal: options.onDismiss ? { ondismiss: options.onDismiss } : undefined,
  });

  rzp.open();
}

export interface CampaignRazorpayArgs {
  payload: DonationCreateOrderPayload;
  description: string;
  /** Called when Razorpay success fires and server verification begins */
  onVerifyStart?: () => void;
  /** Called after verify-payment API succeeds; receives the receipt reference */
  onVerifySuccess: (receiptRef: string) => void;
  /** Called if verification fails */
  onVerifyError?: (error: unknown) => void;
  onDismiss?: () => void;
}

export function useCampaignRazorpayPayment() {
  return useCallback(async (args: CampaignRazorpayArgs) => {
    try {
      const order = await publicDonationPaymentService.createOrder(
        args.payload
      );

      await openRazorpayCheckout({
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency ?? "INR",
        key: order.key_id,
        description: args.description,
        prefill: {
          name: args.payload.name,
          email: args.payload.email,
          contact: args.payload.phone,
        },
        onSuccess: async response => {
          args.onVerifyStart?.();
          try {
            const verifyResult =
              await publicDonationPaymentService.verifyPayment({
                razorpay_order_id: order.order_id,
                razorpay_payment_id: String(response.razorpay_payment_id),
                razorpay_signature: String(response.razorpay_signature),
              });

            const receiptRef =
              verifyResult.receipt_no ??
              response.razorpay_payment_id ??
              order.order_id;
            args.onVerifySuccess(receiptRef);
          } catch (verifyError) {
            toast.error(
              verifyError instanceof Error
                ? verifyError.message
                : "Payment received but verification failed. Please contact support."
            );
            args.onVerifyError?.(verifyError);
          }
        },
        onDismiss: args.onDismiss,
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to start payment. Please try again."
      );
      throw error;
    }
  }, []);
}

/** @deprecated Use useCampaignRazorpayPayment for API-backed donations */
export function useRazorpayCheckout() {
  return useCallback(async () => {
    toast.error(
      "Online payment is only available for active campaigns at this time."
    );
  }, []);
}
